import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Star, Edit3, Trash2, CheckCircle, EyeOff, Copy, Bookmark, Sparkles, FileText, Youtube, Images,
  PlayCircle,
} from 'lucide-react';
import { isNewSkill, isUpdatedSkill, isVideoItem } from './skillsUtils';

/**
 * Card de skill (sistema e Minhas Skills), COMPACTO e adaptativo:
 *   - COM imagens: capa 16:9 mostra UMA imagem inteira por vez; mover o
 *     mouse sobre a capa troca entre elas (zonas invisíveis), com dots e o
 *     rótulo do gerador da imagem ativa. Sem colagem espremida.
 *   - SEM imagens (skills de texto): card curto com chip de ícone.
 * Rodapé enxuto: Copiar (com feedback) + ações em botões de ícone.
 */
const SkillCard = ({
  skill,
  variant = 'system',
  onOpen,
  onCopy,
  onSave,
  onEdit,
  onDelete,
  onToggleFavorite,
  isAdmin = false,
  canalLabel = '',
}) => {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hiddenImgs, setHiddenImgs] = useState({});
  const [activeImg, setActiveImg] = useState(0);
  const coverRef = useRef(null);

  const imgs = (skill.imagens || []).filter((img, i) => img?.url && !hiddenImgs[i]);
  const hasImages = imgs.length > 0;
  const current = Math.min(activeImg, Math.max(0, imgs.length - 1));
  const FallbackIcon = variant === 'user' ? FileText : Sparkles;
  const isUnpublished = variant === 'system' && isAdmin && skill.publicado === false;
  const isFeatured = variant === 'system' ? !!skill.destaque : !!skill.favorito;
  const isSystem = variant === 'system';
  const showNew = isSystem && isNewSkill(skill);
  const showUpdated = isSystem && isUpdatedSkill(skill);
  const plataformas = skill.plataformas || [];
  const copies = isSystem ? (skill.copies_count || 0) : 0;

  const iconBtnStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '26px', height: '26px', borderRadius: '7px',
    border: '1px solid var(--dp-border-subtle)', background: 'transparent',
    color: 'var(--dp-text-secondary)', cursor: 'pointer', transition: 'all 0.2s ease',
    flexShrink: 0,
  };

  const pillStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '2px 8px', borderRadius: '9999px',
    fontSize: '10px', fontWeight: 600, fontFamily: 'var(--dp-font)',
    lineHeight: 1.4, whiteSpace: 'nowrap', flexShrink: 0,
  };

  // Zonas invisíveis: a posição X do mouse na capa escolhe a imagem ativa.
  const handleCoverMove = (e) => {
    if (imgs.length < 2 || !coverRef.current) return;
    const rect = coverRef.current.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const idx = Math.max(0, Math.min(imgs.length - 1, Math.floor(ratio * imgs.length)));
    if (idx !== activeImg) setActiveImg(idx);
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await onCopy(skill);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // toast de erro é responsabilidade do onCopy
    }
  };

  const tutorialPill = skill.video_url ? (
    <span style={{
      ...pillStyle,
      background: hasImages ? 'rgba(0, 0, 0, 0.6)' : 'rgba(13, 174, 255, 0.08)',
      backdropFilter: hasImages ? 'blur(4px)' : 'none',
      color: hasImages ? '#F5F5F5' : 'var(--dp-blue-500)',
      border: hasImages ? '1px solid rgba(255, 255, 255, 0.16)' : '1px solid rgba(13, 174, 255, 0.22)',
    }}>
      <Youtube size={9} />
      {t('skills.card.tutorial')}
    </span>
  ) : null;

  const featuredPill = isFeatured ? (
    <span style={{
      ...pillStyle,
      background: 'rgba(251, 191, 36, 0.15)', color: '#FBBF24',
      border: '1px solid rgba(251, 191, 36, 0.35)',
    }}>
      <Star size={9} fill="currentColor" />
    </span>
  ) : null;

  const unpublishedPill = isUnpublished ? (
    <span style={{
      ...pillStyle,
      background: 'rgba(248, 113, 113, 0.15)', color: 'var(--dp-red-500, #F87171)',
      border: '1px solid rgba(248, 113, 113, 0.35)',
    }}>
      <EyeOff size={9} />
      {t('skills.card.unpublished')}
    </span>
  ) : null;

  // Vida de biblioteca: "Nova" (verde) / "Atualizada" (azul), últimos 14 dias.
  const freshPill = showNew ? (
    <span style={{
      ...pillStyle,
      background: 'rgba(52, 211, 153, 0.15)', color: 'var(--dp-green-500, #34D399)',
      border: '1px solid rgba(52, 211, 153, 0.35)',
    }}>
      {t('skills.card.new')}
    </span>
  ) : showUpdated ? (
    <span style={{
      ...pillStyle,
      background: 'rgba(13, 174, 255, 0.12)', color: 'var(--dp-blue-500, #0DAEFF)',
      border: '1px solid rgba(13, 174, 255, 0.3)',
    }}>
      {t('skills.card.updated')}
    </span>
  ) : null;

  return (
    <div
      onClick={() => onOpen(skill)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hasImages ? 'var(--dp-bg-card)' : 'var(--dp-bg-secondary, #0E0E0E)',
        border: `1px solid ${hover ? 'rgba(13, 174, 255, 0.3)' : 'var(--dp-border-subtle)'}`,
        borderRadius: '11px',
        overflow: 'hidden',
        cursor: 'pointer',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Capa 16:9 SOMENTE quando a skill tem imagens: 1 imagem inteira por
          vez; mouse sobre a capa alterna (zonas), dots indicam a posição */}
      {hasImages && (
        <div
          ref={coverRef}
          onMouseMove={handleCoverMove}
          onMouseLeave={() => setActiveImg(0)}
          style={{
            position: 'relative',
            aspectRatio: '16 / 9',
            background: 'linear-gradient(135deg, var(--dp-bg-card), var(--dp-bg-elevated))',
            overflow: 'hidden',
          }}
        >
          {imgs.map((img, i) => (
            isVideoItem(img) ? (
              <video
                key={`${img.url}-${i}`}
                src={img.url}
                muted
                playsInline
                preload="metadata"
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  objectFit: 'cover', display: 'block', background: '#000',
                  opacity: i === current ? 1 : 0,
                  transition: 'opacity 0.18s ease',
                }}
              />
            ) : (
            <img
              key={`${img.url}-${i}`}
              src={img.url}
              alt={img.label || skill.nome}
              loading="lazy"
              onError={() => {
                const idx = (skill.imagens || []).indexOf(img);
                setHiddenImgs((prev) => ({ ...prev, [idx]: true }));
              }}
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                objectFit: 'cover', display: 'block',
                opacity: i === current ? 1 : 0,
                transition: 'opacity 0.18s ease',
              }}
            />
            )
          ))}

          {/* Selo de play quando a mídia ativa é um vídeo de exemplo */}
          {isVideoItem(imgs[current]) && (
            <span style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.18)', pointerEvents: 'none',
            }}>
              <PlayCircle size={30} color="#FFFFFF" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))' }} />
            </span>
          )}

          {/* Badges no topo da capa */}
          <div style={{
            position: 'absolute', top: '7px', left: '7px', right: '7px',
            display: 'flex', alignItems: 'center', gap: '5px',
          }}>
            {freshPill}
            {tutorialPill}
            <span style={{ flex: 1 }} />
            {imgs.length > 1 && (
              <span style={{
                ...pillStyle,
                background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
                color: '#F5F5F5', border: '1px solid rgba(255, 255, 255, 0.16)',
              }}>
                <Images size={9} />
                {imgs.length}
              </span>
            )}
            {featuredPill}
            {unpublishedPill}
          </div>

          {/* Rodapé da capa: rótulo do gerador da imagem ativa + dots */}
          {(imgs[current]?.label || imgs.length > 1) && (
            <div style={{
              position: 'absolute', left: '7px', right: '7px', bottom: '7px',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              {imgs[current]?.label && (
                <span style={{
                  ...pillStyle,
                  background: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(4px)',
                  color: '#F5F5F5', border: '1px solid rgba(255, 255, 255, 0.14)',
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {imgs[current].label}
                </span>
              )}
              <span style={{ flex: 1 }} />
              {imgs.length > 1 && (
                <span style={{ display: 'inline-flex', gap: '4px', flexShrink: 0 }}>
                  {imgs.map((_, i) => (
                    <span key={i} style={{
                      width: '5px', height: '5px', borderRadius: '50%',
                      background: i === current ? '#FFFFFF' : 'rgba(255, 255, 255, 0.45)',
                      boxShadow: '0 0 3px rgba(0,0,0,0.6)',
                      transition: 'background 0.15s ease',
                    }} />
                  ))}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Corpo compacto */}
      <div style={{
        padding: hasImages ? '9px 11px 6px' : '11px 11px 6px',
        display: 'flex', flexDirection: 'column', gap: '4px', flex: 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          {!hasImages && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '26px', height: '26px', borderRadius: '7px', flexShrink: 0,
              background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.2)',
            }}>
              <FallbackIcon size={13} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
            </span>
          )}
          <span style={{
            flex: 1, minWidth: 0,
            fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)',
            fontFamily: 'var(--dp-font)', lineHeight: 1.35,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {skill.nome}
          </span>
          {!hasImages && featuredPill}
          {!hasImages && unpublishedPill}
        </div>

        {skill.descricao && (
          <span style={{
            fontSize: '11.5px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)',
            lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {skill.descricao}
          </span>
        )}

        {/* Selos de plataforma (Veo Flow, Midjourney, ...) */}
        {plataformas.length > 0 && (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {plataformas.slice(0, 2).map((p) => (
              <span key={p} style={{
                padding: '2px 7px', borderRadius: '5px',
                fontSize: '9.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
                color: 'var(--dp-text-secondary)', background: 'var(--dp-bg-elevated)',
                border: '1px solid var(--dp-border-subtle)',
                lineHeight: 1.4, whiteSpace: 'nowrap',
              }}>
                {p}
              </span>
            ))}
            {plataformas.length > 2 && (
              <span style={{
                padding: '2px 7px', borderRadius: '5px',
                fontSize: '9.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
                color: 'var(--dp-text-tertiary)', border: '1px solid var(--dp-border-subtle)',
                lineHeight: 1.4,
              }}>
                +{plataformas.length - 2}
              </span>
            )}
          </div>
        )}

        {(skill.categoria || canalLabel || copies > 0 || (!hasImages && (skill.video_url || freshPill))) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flexWrap: 'wrap' }}>
            {!hasImages && freshPill}
            {!hasImages && tutorialPill}
            {(skill.categoria || canalLabel || copies > 0) && (
              <span style={{
                fontSize: '10.5px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
              }}>
                {[
                  canalLabel,
                  skill.categoria,
                  copies > 0 ? t('skills.card.copies', { count: copies }) : null,
                ].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Rodapé enxuto */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '0 11px 10px' }}>
        <button
          type="button"
          title={t('skills.detail.copy')}
          onClick={handleCopy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '5px 9px', borderRadius: '7px',
            fontSize: '11.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
            cursor: 'pointer', transition: 'all 0.2s ease', lineHeight: 1.4,
            border: copied ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(13, 174, 255, 0.3)',
            background: copied ? 'rgba(52, 211, 153, 0.08)' : 'rgba(13, 174, 255, 0.06)',
            color: copied ? 'var(--dp-green-500, #34D399)' : 'var(--dp-blue-500, #0DAEFF)',
          }}
          onMouseEnter={(e) => { if (!copied) e.currentTarget.style.background = 'rgba(13, 174, 255, 0.13)'; }}
          onMouseLeave={(e) => { if (!copied) e.currentTarget.style.background = 'rgba(13, 174, 255, 0.06)'; }}
        >
          {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
          {copied ? t('skills.detail.copied_short') : t('skills.detail.copy')}
        </button>
        <span style={{ flex: 1 }} />
        {variant === 'system' && (
          <button
            type="button"
            title={t('skills.card.save_hint')}
            onClick={(e) => { e.stopPropagation(); onSave(skill); }}
            style={iconBtnStyle}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-blue-500)'; e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.35)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
          >
            <Bookmark size={12} />
          </button>
        )}
        {variant === 'user' && (
          <button
            type="button"
            title={skill.favorito ? t('skills.my.fav_off') : t('skills.my.fav_on')}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(skill); }}
            style={{
              ...iconBtnStyle,
              color: skill.favorito ? '#FBBF24' : 'var(--dp-text-secondary)',
              borderColor: skill.favorito ? 'rgba(251, 191, 36, 0.35)' : 'var(--dp-border-subtle)',
            }}
          >
            <Star size={12} fill={skill.favorito ? 'currentColor' : 'none'} />
          </button>
        )}
        {(variant === 'user' || isAdmin) && (
          <>
            <button
              type="button"
              title={t('skills.admin.edit_skill')}
              onClick={(e) => { e.stopPropagation(); onEdit(skill); }}
              style={iconBtnStyle}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-blue-500)'; e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.35)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
            >
              <Edit3 size={12} />
            </button>
            <button
              type="button"
              title={t('skills.my.delete')}
              onClick={(e) => { e.stopPropagation(); onDelete(skill); }}
              style={iconBtnStyle}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-red-500, #F87171)'; e.currentTarget.style.borderColor = 'rgba(248, 113, 113, 0.35)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
            >
              <Trash2 size={12} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SkillCard;
